import { useCallback, useEffect, useRef, useState } from 'react';
import { VoiceButton } from './components/VoiceButton';
import { ResultsList } from './components/ResultsList';
import { SelectedDomain } from './components/SelectedDomain';
import { Settings } from './components/Settings';
import { History } from './components/History';
import { AdminPanel } from './components/AdminPanel';
import { AuthBar } from './components/AuthBar';
import { AuthModal } from './components/AuthModal';
import { CloudHistory } from './components/CloudHistory';
import { TrialBanner } from './components/TrialBanner';
import { VerifyEmail } from './components/VerifyEmail';
import { useAuth } from './hooks/useAuth';
import { useFavorites } from './hooks/useFavorites';
import { useHistory, type HistoryEntry } from './hooks/useHistory';
import { useTheme } from './hooks/useTheme';
import { useBulkCheck } from './hooks/useBulkCheck';
import { buildSmnBulkUrl } from './utils/buildSmnBulkUrl';
import { supportsBulkCheck } from './utils/supportsBulkCheck';
import type { AvailabilityMap, UdResult } from './components/ResultsList';

const UD_BULK_SEARCH_FALLBACK = 'https://unstoppabledomains.com/bulk-search';
const UD_CART_URL = 'https://unstoppabledomains.com/cart';
const UD_PENDING_DELETE_URL = 'https://unstoppabledomains.com/marketplace/pending-delete';
import { buildFavoriteUrl } from './utils/buildFavoriteUrl';
import { parseSelection } from './utils/parseSelection';
import { isBrave, micPermissionHelpText } from './utils/platform';
import { isSpeechSupported, startSpeech, type SpeechSession } from './utils/speech';
import type { DomainOption, GenerateResponse } from './types';

const PERMISSION_ERRORS = new Set(['not-allowed', 'service-not-allowed']);

// CSRF cookie reader — matches the pattern used by useAuth / AdminPanel /
// BillingButton. Without echoing X-CSRF-Token on POSTs, the server's
// csrfGuard rejects authenticated mutations with "CSRF token mismatch".
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

const LAST_FAVORITE_KEY = 'aiprompt:lastFavoriteId:v1';
const PASSTHROUGH_KEY = 'aiprompt:passthrough:v1';
const AUTOMATIC_KEY = 'aiprompt:automatic:v1';

type Status =
  | { kind: 'idle' }
  | { kind: 'loading'; message?: string }
  | { kind: 'error'; message: string; tip?: string }
  | { kind: 'info'; message: string };

function buildVoiceError(rawError: string): { message: string; tip?: string } {
  const code = rawError.trim();
  if (PERMISSION_ERRORS.has(code)) {
    return {
      message: 'Microphone access was blocked for this page.',
      tip: micPermissionHelpText(),
    };
  }
  if (code === 'audio-capture') {
    return { message: 'No microphone detected on this device.' };
  }
  if (code === 'no-speech') {
    return { message: 'No speech heard. Try again.' };
  }
  if (code === 'network') {
    if (isBrave()) {
      return {
        message: "Brave blocks Google's speech service by design, so voice input doesn't work in Brave even with permission granted. Use Chrome, Edge, or Safari for voice, or type your request instead.",
      };
    }
    return {
      message: 'Voice recognition needs an internet connection.',
    };
  }
  return { message: `Voice error: ${code}` };
}

export function App() {
  // /verify-email?token=... is the magic-link landing — render the verify
  // screen instead of the main app when that path is loaded. No router
  // needed because the SPA fallback in nginx/Express serves index.html
  // for the path and we branch in-component.
  const [path, setPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const isVerifyRoute = path.startsWith('/verify-email');
  const isAdminRoute = path.startsWith('/admin');

  // Listen for back/forward + programmatic pushState navigations.
  useEffect(() => {
    const onNav = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);

  // Auth state + modal
  const auth = useAuth();
  const [authModal, setAuthModal] = useState<null | 'signup' | 'login'>(null);

  const handleReturnFromVerify = useCallback(() => {
    window.history.replaceState({}, '', '/');
    setPath('/');
    void auth.refresh();
  }, [auth]);

  const handleLeaveAdmin = useCallback(() => {
    window.history.pushState({}, '', '/');
    setPath('/');
  }, []);

  if (isVerifyRoute) {
    return <VerifyEmail onReturn={handleReturnFromVerify} />;
  }
  if (isAdminRoute) {
    // Hard gate — only render the panel for admin. Anyone else gets booted
    // back to the main app (the backend rejects /api/admin/* with 403 anyway,
    // but this stops the panel from even loading and pinging the API).
    if (auth.loading) return <div className="loading">…</div>;
    if (!auth.user || auth.user.role !== 'ADMIN') {
      handleLeaveAdmin();
      return null;
    }
    return <AdminPanel onLeave={handleLeaveAdmin} />;
  }

  return <MainApp auth={auth} authModal={authModal} setAuthModal={setAuthModal} />;
}

interface MainAppProps {
  auth: ReturnType<typeof useAuth>;
  authModal: null | 'signup' | 'login';
  setAuthModal: (v: null | 'signup' | 'login') => void;
}

function MainApp({ auth, authModal, setAuthModal }: MainAppProps) {
  const [requestText, setRequestText] = useState('');
  const [options, setOptions] = useState<DomainOption[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [selectedFavoriteId, setSelectedFavoriteId] = useState<string>(() => {
    if (typeof localStorage === 'undefined') return 'chatgpt';
    return localStorage.getItem(LAST_FAVORITE_KEY) || 'chatgpt';
  });

  // Persist the last-used service so it survives reloads AND history loads.
  useEffect(() => {
    if (selectedFavoriteId) {
      try {
        localStorage.setItem(LAST_FAVORITE_KEY, selectedFavoriteId);
      } catch {
        /* storage unavailable; ignore */
      }
    }
  }, [selectedFavoriteId]);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cloudHistoryOpen, setCloudHistoryOpen] = useState(false);
  const [currentHistoryId, setCurrentHistoryId] = useState<string | null>(null);
  const [selectionListenerOn, setSelectionListenerOn] = useState(false);

  const [passthrough, setPassthrough] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(PASSTHROUGH_KEY) === 'true';
  });
  const [automatic, setAutomatic] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(AUTOMATIC_KEY) === 'true';
  });

  useEffect(() => {
    try { localStorage.setItem(PASSTHROUGH_KEY, String(passthrough)); } catch { /* ignore */ }
  }, [passthrough]);
  useEffect(() => {
    try { localStorage.setItem(AUTOMATIC_KEY, String(automatic)); } catch { /* ignore */ }
  }, [automatic]);

  const selectionSessionRef = useRef<SpeechSession | null>(null);
  const speechSupported = isSpeechSupported();

  // Hero video starts muted (autoplay policy + per-spec default). User can
  // toggle audio via the bottom-right speaker button; React useState manages
  // the visual state, and we sync the actual <video>.muted via the ref
  // because the React `muted` prop is occasionally flaky when toggled.
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const [audioOn, setAudioOn] = useState(false);

  useEffect(() => {
    const el = heroVideoRef.current;
    if (!el) return;
    el.muted = !audioOn;
    if (audioOn) {
      el.play().catch(() => {
        // Some browsers (notably iOS PWA) refuse to keep playing after
        // unmute if there was no fresh user gesture. The click on the
        // speaker button IS a user gesture so this normally works.
      });
    }
  }, [audioOn]);

  const { favorites, addFavorite, updateFavorite, deleteFavorite, resetDefaults } =
    useFavorites();
  const history = useHistory();
  const { theme, toggle: toggleTheme } = useTheme();
  const { bulkCheckEnabled, setBulkCheckEnabled } = useBulkCheck();
  const [availability, setAvailability] = useState<AvailabilityMap | null>(null);
  const [udBulkBusy, setUdBulkBusy] = useState(false);
  // Wipe availability whenever the 10-options reference changes — a
  // fresh Generate or a History load is a new set of names and the old
  // badges would mislead. Cheap effect; runs at most a few times per
  // session.
  useEffect(() => {
    setAvailability(null);
  }, [options]);

  // Keep the favorite picker valid if the selected favorite was deleted.
  useEffect(() => {
    if (!favorites.find((f) => f.id === selectedFavoriteId)) {
      setSelectedFavoriteId(favorites[0]?.id ?? '');
    }
  }, [favorites, selectedFavoriteId]);

  const handleSelectByNumber = useCallback(
    (n: number) => {
      const match = options.find((opt) => opt.number === n);
      if (!match) {
        setStatus({ kind: 'error', message: `No option numbered ${n}.` });
        return;
      }
      setSelectedNumber(n);
      setStatus({ kind: 'info', message: `Selected ${match.domain}.` });
      if (currentHistoryId) {
        history.updateSelection(currentHistoryId, n);
      }
    },
    [options, currentHistoryId, history]
  );

  const handleSaveToHistory = useCallback(() => {
    if (!requestText.trim() || options.length === 0) return;
    // Update in place if this set was loaded from history; otherwise add new.
    if (currentHistoryId && history.entries.some((e) => e.id === currentHistoryId)) {
      history.updateSelection(currentHistoryId, selectedNumber);
      setStatus({ kind: 'info', message: 'History entry updated.' });
      return;
    }
    const id = history.add({
      request: requestText.trim(),
      options,
      selectedNumber,
    });
    setCurrentHistoryId(id);
    setStatus({ kind: 'info', message: 'Saved to history.' });
  }, [requestText, options, selectedNumber, currentHistoryId, history]);

  const handleLoadFromHistory = useCallback(
    (entry: HistoryEntry) => {
      setRequestText(entry.request);
      setOptions(entry.options);
      setSelectedNumber(entry.selectedNumber);
      setCurrentHistoryId(entry.id);
      setHistoryOpen(false);
      // selectedFavoriteId is intentionally NOT changed here so the
      // last-used service is remembered regardless of history.
      setStatus({
        kind: 'info',
        message: entry.selectedNumber
          ? `Loaded from history. #${entry.selectedNumber} pre-selected, pick a service and click Open.`
          : 'Loaded from history. Pick a number, then a service, then Open.',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    []
  );

  const stopSelectionListener = useCallback(() => {
    selectionSessionRef.current?.stop();
    selectionSessionRef.current = null;
    setSelectionListenerOn(false);
  }, []);

  const startSelectionListener = useCallback(() => {
    if (!speechSupported) return;
    setSelectionListenerOn(true);
    selectionSessionRef.current = startSpeech({
      onResult: (transcript) => {
        const n = parseSelection(transcript);
        if (n != null) {
          handleSelectByNumber(n);
        } else {
          setStatus({
            kind: 'info',
            message: `Heard "${transcript}". Say a number 1 through 10.`,
          });
        }
      },
      onError: (msg) => {
        setStatus({ kind: 'error', ...buildVoiceError(msg) });
        stopSelectionListener();
      },
      onEnd: () => {
        selectionSessionRef.current = null;
        setSelectionListenerOn(false);
      },
    });
    if (!selectionSessionRef.current) {
      setSelectionListenerOn(false);
    }
  }, [handleSelectByNumber, speechSupported, stopSelectionListener]);

  useEffect(() => {
    return () => stopSelectionListener();
  }, [stopSelectionListener]);

  const handleGenerate = useCallback(async () => {
    const text = requestText.trim();
    if (!text) {
      setStatus({ kind: 'error', message: 'Type or speak a request first.' });
      return;
    }
    setStatus({ kind: 'loading' });
    setOptions([]);
    setSelectedNumber(null);
    setCurrentHistoryId(null);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      // Authenticated users (any tier) hit csrfGuard on this POST — echo the
      // double-submit token from the aiprompt_csrf cookie. Anonymous callers
      // have no session so csrfGuard skips; omitting the header is fine.
      const csrf = readCookie('aiprompt_csrf');
      if (csrf) headers['X-CSRF-Token'] = csrf;
      const res = await fetch('/api/generate', {
        method: 'POST',
        headers,
        credentials: 'same-origin',
        body: JSON.stringify({ request: text }),
      });
      const data = (await res.json()) as GenerateResponse & {
        error?: string | { code?: string; message?: string };
      };
      if (!res.ok) {
        // Server emits errors as { error: { code, message } } — unwrap the
        // message so the user sees the friendly text instead of [object Object].
        // Strings are tolerated for backwards-compat with older callers.
        const errMessage =
          typeof data.error === 'string'
            ? data.error
            : data.error?.message || `Server returned ${res.status}.`;
        throw new Error(errMessage);
      }
      if (!data.options || data.options.length === 0) {
        throw new Error('No options returned.');
      }
      setOptions(data.options);
      setStatus({
        kind: 'info',
        message: `Got ${data.options.length} ideas. Pick one, or say "number 8".`,
      });
      // Re-fetch the auth/quota state so the header pill ticks down
      // immediately rather than waiting for the next page-load.
      void auth.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setStatus({ kind: 'error', message });
    }
  }, [requestText, auth]);

  const handlePassthrough = useCallback(
    (overrideText?: string) => {
      const text = (overrideText ?? requestText).trim();
      if (!text) {
        setStatus({ kind: 'error', message: 'Type or speak a request first.' });
        return;
      }
      const fav =
        favorites.find((f) => f.id === selectedFavoriteId) ?? favorites[0];
      if (!fav) {
        setStatus({
          kind: 'error',
          message: 'No service selected. Add one in Settings.',
        });
        return;
      }
      const url = buildFavoriteUrl(fav.template, text);
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => {
          /* ignore */
        });
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      setStatus({
        kind: 'info',
        message: `Sent to ${fav.name}. Value copied to clipboard.`,
      });
    },
    [requestText, favorites, selectedFavoriteId]
  );

  const handleRequestTranscript = (transcript: string) => {
    setRequestText(transcript);
    if (passthrough && automatic) {
      handlePassthrough(transcript);
      return;
    }
    setStatus({
      kind: 'info',
      message: passthrough
        ? 'Heard your request. Click Send.'
        : 'Heard your request. Click Generate.',
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (passthrough) handlePassthrough();
      else handleGenerate();
    }
  };

  // If the request text changes to look like a selection, auto-apply it.
  const handleRequestChange = (value: string) => {
    setRequestText(value);
    if (options.length > 0) {
      const n = parseSelection(value);
      if (n != null) {
        handleSelectByNumber(n);
      }
    }
  };

  const selectedOption =
    options.find((opt) => opt.number === selectedNumber) ?? null;

  // Active favorite + its bulk-check mode (smn-deeplink | ud-proxy | null).
  // Derived so they react immediately to dropdown changes without effects.
  const currentFavorite =
    favorites.find((f) => f.id === selectedFavoriteId) ?? favorites[0];
  const bulkMode = supportsBulkCheck(currentFavorite);

  const isPaid =
    !!auth.user &&
    (auth.user.role === 'SUBSCRIBER' ||
      auth.user.role === 'ADMIN' ||
      auth.user.lifetimeFree);

  // CTA visibility: toggle is on, the active favorite supports bulk-check,
  // and there are options on screen to check.
  const showBulkCheckCta =
    bulkCheckEnabled && options.length > 0 && bulkMode !== null;

  const bulkCtaLabel =
    bulkMode === 'ud-proxy'
      ? udBulkBusy
        ? 'Checking…'
        : 'Check 10 on Unstoppable Domains'
      : 'Check 10 on SecureMyName ↗';

  // No-key fallback: open UD's public bulk-search page in a new tab.
  // Used whenever the user isn't authenticated against the proxy
  // (anonymous, Guest tier, or paid-without-key). The status banner
  // tells them about the inline-badges upgrade path through Settings.
  function openUdBulkSearchFallback(reason: string) {
    window.open(UD_BULK_SEARCH_FALLBACK, '_blank', 'noopener,noreferrer');
    setStatus({ kind: 'info', message: reason });
  }

  async function handleUdBulkCheck() {
    // Anyone without a usable key falls back to UD's public bulk search.
    if (!auth.user) {
      openUdBulkSearchFallback(
        'Opened Unstoppable Domains bulk search. Sign in to AiPrompt.si and add a UD MCP key for inline availability badges.',
      );
      return;
    }
    if (!isPaid) {
      openUdBulkSearchFallback(
        'Opened Unstoppable Domains bulk search. Subscribe + add a UD MCP key to see availability inline.',
      );
      return;
    }
    if (!auth.user.udMcpApiKeyLast4) {
      openUdBulkSearchFallback(
        'Opened Unstoppable Domains bulk search. Add your UD MCP key in Settings to see availability inline next time.',
      );
      return;
    }

    setUdBulkBusy(true);
    setStatus({
      kind: 'loading',
      message: 'Checking availability at Unstoppable Domains…',
    });
    try {
      // Send the FULL domain name per idea (with whatever TLD it has),
      // not just stems — so history-loaded mixes like example.com / example.io
      // / example.si each get checked at their own TLD. Backend parses
      // these into (stem, tld) pairs and batches the MCP call.
      const domains = options.slice(0, 10).map((o) => {
        const raw = String(o.domain || '')
          .trim()
          .toLowerCase();
        return raw.includes('.') ? raw : `${raw}.com`;
      });
      const csrf = document.cookie.match(/(?:^|; )aiprompt_csrf=([^;]*)/);
      const csrfVal = csrf ? decodeURIComponent(csrf[1]) : '';
      const res = await fetch('/api/ud/bulk-search', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfVal ? { 'X-CSRF-Token': csrfVal } : {}),
        },
        body: JSON.stringify({ domains }),
      });
      const json = await res.json();
      if (!res.ok) {
        if (json?.error?.code === 'no_ud_key') {
          // Key was revoked between bulkcheck-cta render and this call.
          // Fall back to the public page instead of leaving the user
          // staring at an error.
          openUdBulkSearchFallback(
            'Your UD MCP key is no longer on file. Opened the public bulk search instead.',
          );
          return;
        }
        setStatus({
          kind: 'error',
          message:
            json?.error?.message || `Bulk check failed (HTTP ${res.status}).`,
        });
        setAvailability(null);
        return;
      }
      const map: AvailabilityMap = json.availability || {};
      setAvailability(map);
      const counts = Object.values(map).reduce(
        (acc, r) => {
          acc[r.state] = (acc[r.state] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
      const buyable =
        (counts.register || 0) +
        (counts.aftermarket || 0) +
        (counts.backorder || 0);
      setStatus({
        kind: 'info',
        message: `Unstoppable Domains: ${buyable} of ${domains.length} names buyable (${counts.register || 0} register, ${counts.aftermarket || 0} aftermarket, ${counts['taken-broker'] || 0} taken).`,
      });
    } catch (err) {
      setStatus({
        kind: 'error',
        message:
          err instanceof Error ? err.message : 'Bulk check failed unexpectedly.',
      });
      setAvailability(null);
    } finally {
      setUdBulkBusy(false);
    }
  }

  // Pill click dispatcher. Register hits the AiPrompt.si cart-add proxy first;
  // all other states are a straight window.open to the right UD page.
  async function handlePillClick(result: UdResult) {
    if (result.state === 'register') {
      const domain = result.domain;
      setStatus({
        kind: 'loading',
        message: `Adding ${domain} to your Unstoppable Domains cart…`,
      });
      try {
        const csrf = document.cookie.match(/(?:^|; )aiprompt_csrf=([^;]*)/);
        const csrfVal = csrf ? decodeURIComponent(csrf[1]) : '';
        const res = await fetch('/api/ud/cart/add', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            ...(csrfVal ? { 'X-CSRF-Token': csrfVal } : {}),
          },
          body: JSON.stringify({ domain }),
        });
        const json = await res.json();
        if (!res.ok) {
          setStatus({
            kind: 'error',
            message:
              json?.error?.message || `Cart add failed (HTTP ${res.status}).`,
          });
          return;
        }
        window.open(json.cartUrl || UD_CART_URL, '_blank', 'noopener,noreferrer');
        const tail = json.cartTotal ? ` Cart total: ${json.cartTotal}.` : '';
        setStatus({
          kind: 'info',
          message: `${domain} added to your Unstoppable Domains cart — checkout opened.${tail}`,
        });
      } catch (err) {
        setStatus({
          kind: 'error',
          message:
            err instanceof Error ? err.message : 'Cart add failed unexpectedly.',
        });
      }
      return;
    }
    if (result.state === 'aftermarket' || result.state === 'taken-broker') {
      window.open(result.buyUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (result.state === 'backorder') {
      window.open(UD_PENDING_DELETE_URL, '_blank', 'noopener,noreferrer');
      return;
    }
  }

  const handleBulkCheck = () => {
    if (bulkMode === 'ud-proxy') {
      void handleUdBulkCheck();
      return;
    }
    // smn-deeplink path: open SMN with all 10 stems prefilled. SMN reads
    // bulk=1 + names on mount, auto-fires runSearch(), and renders the
    // results against the user's sticky TLD preference. New tab so AiPrompt.si
    // stays open for more brainstorming.
    const url = buildSmnBulkUrl(options);
    window.open(url, '_blank', 'noopener,noreferrer');
    setStatus({
      kind: 'info',
      message:
        'Opened SecureMyName with all 10 names — bulk check is running there.',
    });
  };

  const handleScrollTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="app" id="top">
      <header className="header">
        <div className="header__brand">
          <span className="header__mark" aria-hidden="true">●</span>
          <span className="header__title">AiPrompt.si</span>
        </div>
        <div className="header__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={async () => {
              // Hard refresh: best-effort clear any Cache Storage entries
              // (so a future service worker can't serve stale assets), then
              // reload the page with a unique query parameter so the
              // browser's HTTP cache misses too. window.location.replace
              // keeps the new URL out of the back-stack.
              try {
                if ('caches' in window) {
                  const names = await caches.keys();
                  await Promise.all(names.map((n) => caches.delete(n)));
                }
              } catch (_) {
                /* best-effort cleanup; reload regardless */
              }
              const url = new URL(window.location.href);
              url.searchParams.set('_r', Date.now().toString());
              window.location.replace(url.toString());
            }}
            aria-label="Hard refresh"
            title="Hard refresh — bypass cache and reload"
          >
            <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <span className="header__label">Refresh</span>
          </button>
          <button
            type="button"
            className="button button--ghost theme-toggle"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? (
              <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="5" />
                <line x1="12" y1="1" x2="12" y2="3" />
                <line x1="12" y1="21" x2="12" y2="23" />
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                <line x1="1" y1="12" x2="3" y2="12" />
                <line x1="21" y1="12" x2="23" y2="12" />
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
              </svg>
            ) : (
              <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
            <span className="header__label">{theme === 'dark' ? 'Light' : 'Dark'}</span>
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              // Anonymous users can SAVE to history (their entries persist in
              // localStorage and unlock the moment they sign up) but the modal
              // itself is gated to push the sign-up conversion.
              if (!auth.user) {
                setAuthModal('login');
                setStatus({ kind: 'info', message: 'Sign in to view your saved history.' });
                return;
              }
              setHistoryOpen(true);
            }}
            title={!auth.user ? 'Sign in to view your saved history' : 'History'}
            aria-label="History"
          >
            <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            <span className="header__label">History</span>
            {history.entries.length > 0 && (
              <span className="header__count"> ({history.entries.length})</span>
            )}
          </button>
          {auth.user && (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => {
                if (!auth.user) return;
                // Guest tier sees the Cloud button (discovery) but
                // clicking it surfaces an upgrade nudge instead of
                // opening the modal. Their generations still save
                // server-side and will appear here the moment they
                // upgrade — Subscriber/Admin see the modal directly.
                if (auth.user.role === 'GUEST') {
                  setStatus({
                    kind: 'info',
                    message:
                      'Cloud history is a Subscriber feature. Tap Upgrade in the header to unlock — your previous generations are already saved and will appear the moment you upgrade.',
                  });
                  return;
                }
                setCloudHistoryOpen(true);
              }}
              title={
                auth.user.role === 'GUEST'
                  ? 'Subscribe to view your last 25 generations across devices'
                  : 'Cloud — your last 25 generations across devices'
              }
              aria-label="Cloud history"
            >
              <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
              </svg>
              <span className="header__label">Cloud</span>
            </button>
          )}
          <button
            type="button"
            className="button button--ghost"
            onClick={() => {
              if (!auth.user) {
                setAuthModal('login');
                setStatus({ kind: 'info', message: 'Sign in to manage your favorite services.' });
                return;
              }
              setSettingsOpen(true);
            }}
            title={!auth.user ? 'Sign in to manage your favorite services' : 'Settings'}
            aria-label="Settings"
          >
            <svg className="header__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span className="header__label">Settings</span>
          </button>
          <AuthBar
            user={auth.user}
            quota={auth.quota}
            loading={auth.loading}
            onOpenAuth={(tab) => setAuthModal(tab)}
            onLogout={auth.logout}
          />
        </div>
      </header>

      <div className="hero" aria-label="Hero animation">
        <video
          ref={heroVideoRef}
          className="hero__video"
          src="/aiprompt.mp4?v=1"
          poster="/aiprompt-poster.jpg?v=1"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        />
        <button
          type="button"
          className="hero__audio"
          onClick={() => setAudioOn((v) => !v)}
          aria-label={audioOn ? 'Mute hero video' : 'Unmute hero video'}
          aria-pressed={audioOn}
          title={audioOn ? 'Mute' : 'Unmute'}
        >
          {audioOn ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          )}
        </button>
      </div>

      <main className="main">
        {auth.user && <TrialBanner user={auth.user} />}
        <section className="card">
          <h1 className="card__title">What are you brainstorming?</h1>
          <p className="card__subtitle">
            Speak or type a request. Get 10 ideas.
          </p>

          <div className="input-row">
            <VoiceButton
              label="Speak request"
              listeningLabel="Listening…"
              onTranscript={handleRequestTranscript}
              onError={(msg) => setStatus({ kind: 'error', ...buildVoiceError(msg) })}
              disabled={status.kind === 'loading'}
            />
            <select
              className="service-picker"
              value={selectedFavoriteId || favorites[0]?.id || ''}
              onChange={(e) => setSelectedFavoriteId(e.target.value)}
              aria-label="Service where ideas will open or send"
              title="Service where ideas will open or send"
            >
              {favorites.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            {bulkMode !== null && (
              <label
                className="bulk-check-toggle"
                title={
                  bulkMode === 'ud-proxy'
                    ? 'Check availability of all 10 generated ideas via Unstoppable Domains (requires a saved UD MCP key in Settings)'
                    : 'Check availability of all 10 generated ideas in one bulk call to SecureMyName'
                }
              >
                <input
                  type="checkbox"
                  checked={bulkCheckEnabled}
                  onChange={(e) => setBulkCheckEnabled(e.target.checked)}
                />
                <span>Bulk check</span>
              </label>
            )}
          </div>
          <textarea
            className="text-input"
            value={requestText}
            onChange={(e) => handleRequestChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='e.g. "Find me domains for an AI startup" or "Product names for a coffee subscription"'
            rows={2}
          />

          <div className="modes-row">
            <label className="mode-checkbox">
              <input
                type="checkbox"
                checked={passthrough}
                onChange={(e) => setPassthrough(e.target.checked)}
              />
              <span>Passthrough</span>
            </label>
            <label
              className={`mode-checkbox${!passthrough ? ' mode-checkbox--disabled' : ''}`}
              title={
                passthrough
                  ? 'Auto-send the voice transcript as soon as it arrives'
                  : 'Enable Passthrough first'
              }
            >
              <input
                type="checkbox"
                checked={automatic}
                onChange={(e) => setAutomatic(e.target.checked)}
                disabled={!passthrough}
              />
              <span>Automatic</span>
            </label>
          </div>

          <div className="button-row">
            <button
              type="button"
              className="button button--primary button--large"
              onClick={passthrough ? () => handlePassthrough() : handleGenerate}
              disabled={status.kind === 'loading' || !requestText.trim()}
            >
              {status.kind === 'loading'
                ? 'Generating…'
                : passthrough
                  ? 'Send'
                  : 'Generate 10 ideas'}
            </button>
            {speechSupported && options.length > 0 && (
              <button
                type="button"
                className={`button button--ghost${selectionListenerOn ? ' button--active' : ''}`}
                onClick={
                  selectionListenerOn ? stopSelectionListener : startSelectionListener
                }
              >
                {selectionListenerOn ? 'Stop listening' : 'Listen for selection'}
              </button>
            )}
          </div>

          {status.kind !== 'idle' && (
            <div className={`status status--${status.kind}`}>
              <div className="status__message">
                {status.kind === 'loading'
                  ? status.message || 'Asking AiPrompt.si for 10 ideas…'
                  : status.message}
              </div>
              {status.kind === 'error' && status.tip && (
                <div className="status__tip">{status.tip}</div>
              )}
            </div>
          )}
        </section>

        {options.length > 0 && (
          <section className="card">
            <div className="card__heading-row">
              <h2 className="card__heading">Ideas</h2>
              <div className="card__heading-actions">
                {showBulkCheckCta && (
                  <button
                    type="button"
                    className="button button--primary button--small"
                    onClick={handleBulkCheck}
                    title="Open SecureMyName with all 10 names prefilled for a single-shot bulk availability check"
                  >
                    {bulkCtaLabel}
                  </button>
                )}
                <button
                  type="button"
                  className="button button--ghost button--small"
                  onClick={handleSaveToHistory}
                  disabled={
                    currentHistoryId !== null &&
                    history.entries.some((e) => e.id === currentHistoryId)
                  }
                  title={
                    currentHistoryId &&
                    history.entries.some((e) => e.id === currentHistoryId)
                      ? 'Already in history'
                      : 'Save the prompt and these 10 results to History'
                  }
                >
                  {currentHistoryId &&
                  history.entries.some((e) => e.id === currentHistoryId)
                    ? 'Saved'
                    : 'Save to History'}
                </button>
              </div>
            </div>
            <ResultsList
              options={options}
              selectedNumber={selectedNumber}
              onSelect={handleSelectByNumber}
              availability={availability}
              onPillClick={handlePillClick}
            />
            {showBulkCheckCta && (
              <div className="card__bulk-footer">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={handleBulkCheck}
                  title="Open SecureMyName with all 10 names prefilled for a single-shot bulk availability check"
                >
                  {bulkCtaLabel}
                </button>
              </div>
            )}
          </section>
        )}

        {options.length > 0 && (
          <section className="card">
            <h2 className="card__heading">Action</h2>
            <SelectedDomain
              option={selectedOption}
              favorites={favorites}
              selectedFavoriteId={selectedFavoriteId || favorites[0]?.id || ''}
              onFavoriteChange={setSelectedFavoriteId}
              onOpened={(value) =>
                setStatus({
                  kind: 'info',
                  message: `Opened on ${
                    favorites.find((f) => f.id === selectedFavoriteId)?.name ?? 'site'
                  }. Value copied to clipboard${value.length > 60 ? ' (long prompt: paste if it didn’t pre-fill)' : ''}.`,
                })
              }
            />
          </section>
        )}

      </main>

      <footer className="footer">
        <a href="/about.html" className="footer__link">About</a>
        <span className="footer__sep">&middot;</span>
        <a href="/faq.html" className="footer__link">FAQ</a>
        <span className="footer__sep">&middot;</span>
        <a href="/contact.html" className="footer__link">Contact</a>
        <span className="footer__sep">&middot;</span>
        <a href="#top" className="footer__link" onClick={handleScrollTop}>
          &copy; AiPrompt.si
        </a>
      </footer>

      {settingsOpen && (
        <Settings
          favorites={favorites}
          onAdd={addFavorite}
          onUpdate={updateFavorite}
          onDelete={deleteFavorite}
          onResetDefaults={resetDefaults}
          onClose={() => setSettingsOpen(false)}
          user={auth.user}
          isPaid={isPaid}
          onUdKeyChange={() => void auth.refresh()}
        />
      )}

      {historyOpen && (
        <History
          entries={history.entries}
          onLoad={handleLoadFromHistory}
          onDelete={history.remove}
          onClearAll={history.clearAll}
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {authModal && (
        <AuthModal
          initialTab={authModal}
          onSignup={auth.signup}
          onLogin={auth.login}
          onClose={() => setAuthModal(null)}
        />
      )}

      {cloudHistoryOpen && (
        <CloudHistory
          onLoad={(entry) => {
            setRequestText(entry.request);
            setOptions(entry.options);
            setSelectedNumber(null);
            setCurrentHistoryId(null);
            setStatus({ kind: 'info', message: 'Loaded from cloud history.' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onClose={() => setCloudHistoryOpen(false)}
        />
      )}

      {/* Floating selection-mic — same toggle as the inline "Listen for
          selection" button next to Generate, but follows the user down the
          page so they can say "number 8" while scrolled to the option list
          or the SelectedDomain card without scrolling back up. Renders only
          when there are options on screen and Web Speech is supported. */}
      {speechSupported && options.length > 0 && (
        <button
          type="button"
          className={`fab-listen${selectionListenerOn ? ' fab-listen--active' : ''}`}
          onClick={
            selectionListenerOn ? stopSelectionListener : startSelectionListener
          }
          aria-label={
            selectionListenerOn
              ? 'Stop listening for selection'
              : 'Say a number to select an option'
          }
          title={
            selectionListenerOn
              ? 'Stop listening'
              : 'Listen for selection — say a number 1 through 10'
          }
        >
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
      )}
    </div>
  );
}
